/**
 * Frontend API Configuration
 *
 * Single source of truth for all backend service URLs.
 * In development, points to local Firebase Emulators (localhost:5001).
 * In production, points to HemoLogix Cloud Functions on GCP.
 */

// Use import.meta.env for Vite build-time environment variables.
// These are replaced with process.env in functions/ via copy-shared.mjs.

const IS_DEV = import.meta.env.MODE === 'development';
const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'gen-lang-client-0571783648';
const REGION = 'us-central1';

/** Base URL for all Firebase Cloud Functions */
export const CF_BASE_URL = IS_DEV
  ? `http://127.0.0.1:5001/${PROJECT_ID}/${REGION}`
  : `https://${REGION}-${PROJECT_ID}.cloudfunctions.net`;

/**
 * Endpoint Registry
 * Maps semantic service names to their full Cloud Function URLs.
 */
export const API_ENDPOINTS = {
  /** Intake Nurse chat endpoint (autonomous clinical awareness) */
  nurseChat: `${CF_BASE_URL}/platinumNurseChat`,

  /** Nurse Proxy (server-side Gemini bridge for security) */
  nurseProxy: `${CF_BASE_URL}/nurseProxy`,

  /** Claude API Proxy */
  claudeProxy: `${CF_BASE_URL}/claudeProxy`,

  /** Gemini API Proxy */
  geminiProxy: `${CF_BASE_URL}/geminiProxy`,

  /** Qwen Expert Proxy (architectural decision support) */
  qwenProxy: `${CF_BASE_URL}/qwenProxy`,

  /** Lab extraction pipeline (CAP.08) — direct Cloud Run URL bypasses cloudfunctions.net proxy */
  extractLab: 'https://extractlabpdf-w4ggvbfdga-uc.a.run.app',

  /** Stripe checkout session creation */
  createCheckout: `${CF_BASE_URL}/createCheckoutSession`,

  /** Magic link authentication (passwordless) */
  sendMagicLink: `${CF_BASE_URL}/sendMagicLink`,

  /** Patient profile hydration */
  getProfile: `${CF_BASE_URL}/getPatientProfile`,

  /** Clinical audit logging (HLX-C13) */
  logAudit: `${CF_BASE_URL}/logClinicalAudit`,
  
  // Legacy mappings for older code expecting these specific names
  extractLabPDF: 'https://extractlabpdf-w4ggvbfdga-uc.a.run.app',
  startCheckout: `${CF_BASE_URL}/createCheckoutSession`,
};

/** Legacy alias for API_ENDPOINTS */
export const CF_URLS = API_ENDPOINTS;

/** Returns the web app's base URL (origin) for constructing absolute URLs */
export function resolveWebAppUrl(): string {
  return window.location.origin;
}

/**
 * API Headers
 * Standard headers included in all fetch calls to the backend.
 */
export const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-Hemologix-Client': 'web-v3-platinum',
};
